import React from 'react';
import { BookOpen, ArrowRight, Calendar, Tag, User, Clock, Eye, Brain } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningArticles } from '../../hooks/useLearningArticles';
import SkeletonLoader from '../common/SkeletonLoader';

const ThinkingTrainingSection: React.FC = () => {
  console.log('🧠 ThinkingTrainingSection组件渲染');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { data: articles, isLoading, error } = useLearningArticles({ limit: 3 });
  
  console.log('📚 ThinkingTrainingSection状态:', {
    hasUser: !!user,
    authLoading,
    isLoading,
    hasError: !!error,
    articlesCount: articles?.length || 0
  });
  
  if (error) {
    console.error('❌ ThinkingTrainingSection错误:', error);
  }

  const handleArticleClick = (article: any) => {
    // 跳转到学习文章详情页面
    navigate(`/learning/article/${article.id}`);
  };

  const handleViewMore = () => {
    // 直接跳转到文章列表页面，无论是否登录
    navigate('/learning/articles');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // 估算阅读时间（基于字符数）
  const estimateReadingTime = (content: string) => {
    if (!content) return 5;
    return Math.ceil(content.length / 300) || 5;
  };

  return (
    <section id="thinking-training" className="py-12 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          {/* 增强的标题区域 */}
          <div className="inline-flex items-center px-4 py-2 bg-green-50 border border-green-200/50 rounded-full text-sm font-medium mb-4 shadow-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-green-700 font-semibold">思维提升专区</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 via-blue-600 to-purple-600">
              思维训练
            </span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            通过精选学习文章，系统性提升思维能力和认知水平
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <SkeletonLoader key={i} className="h-64" />
            ))}
          </div>
        ) : articles && articles.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {articles.map((article) => (
                <div
                  key={article.id}
                  onClick={() => handleArticleClick(article)}
                  className="bg-white rounded-lg shadow-sm hover:shadow-xl hover:border-green-300 hover:-translate-y-1 transition-all duration-300 border cursor-pointer group overflow-hidden"
                >
                  {/* 顶部渐变条 */}
                  <div className="h-1 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500"></div>
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-blue-600 rounded flex items-center justify-center">
                          <Brain className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm text-green-600 font-medium">
                          思维训练
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {formatDate(article.created_at)}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-green-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {article.summary || '通过系统性学习，提升思维认知能力'}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <BookOpen className="w-3 h-3" />
                        <span>学习文章</span>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <span className="flex items-center">
                          <Eye className="w-3 h-3 mr-1" />
                          {article.read_count || Math.floor(Math.random() * 500) + 50}
                        </span>
                        <span className="px-2 py-1 bg-gradient-to-r from-blue-100 to-green-100 text-blue-800 rounded-full font-semibold">
                          {estimateReadingTime(article.content)}分钟阅读
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <button
                onClick={handleViewMore}
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors duration-200"
              >
                {user ? '查看所有文章' : '查看更多文章'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
              <Brain className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂无学习文章</h3>
            <p className="text-gray-500">精彩的思维训练内容即将上线，敬请期待</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ThinkingTrainingSection;